// src/components/ChatWidget.tsx
import React, { useState, useEffect, useRef, useMemo, memo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';
import {
  MessageCircle, X, Send, User, CheckCheck,
  Smile, Paperclip, FileText, Image, Download, Eye, Trash2
} from 'lucide-react';

interface User {
  id: string;
  name: string;
  email: string;
  photo?: string;
  online: boolean;
}

interface Message {
  id: string;
  from: string;
  to: string;
  text: string;
  file?: {
    name: string;
    url: string;
    size: number;
    mimeType: string;
  };
  read: boolean;
  createdAt: string;
}

const sanitizeText = (text: string): string => {
  return text.replace(/<[^>]*>/g, '');
};

const POPULAR_EMOJIS = new Set([
  '😊', '😂', '❤️', '👍', '🔥', '🎉', '😢', '😡',
  '🥳', '😎', '😍', '🥺', '🤔', '😱', '💀', '👋',
  '🙏', '💪', '👏', '🤝', '💯', '✨', '⭐', '🌈',
  '🍕', '🎸', '🏀', '⚽', '🐱', '🐶', '🌸', '🌟'
]);

const isOnlyEmoji = (text: string): boolean => {
  const trimmed = text.trim();
  return POPULAR_EMOJIS.has(trimmed);
};

// Мемоизированный компонент пользователя
const UserItem = memo(({ user, isSelected, hasUnread, unreadCount, onSelect }: {
  user: User;
  isSelected: boolean;
  hasUnread: boolean;
  unreadCount?: number;
  onSelect: () => void;
}) => {
  return (
    <div
      onClick={onSelect}
      className="chat-user-item"
      style={{
        ...styles.userItem,
        backgroundColor: isSelected 
          ? 'rgba(37,117,252,0.15)' 
          : hasUnread 
            ? 'rgba(255, 77, 79, 0.15)' 
            : 'transparent',
        borderRadius: '30px',
        margin: '4px 8px',
      }}
    >
      <div style={styles.userAvatarWrapper}>
        <div style={styles.userAvatar}>
          {user.photo ? <img src={user.photo} alt={user.name} style={styles.avatarImg} /> : <User size={18} />}
        </div>
        <span style={{ ...styles.onlineDot, backgroundColor: user.online ? '#52c41a' : '#ccc' }} />
        {unreadCount && unreadCount > 0 ? (
          <span style={styles.avatarBadge}>{unreadCount}</span>
        ) : null}
      </div>
      <div style={styles.userInfo}>
        <div style={styles.userName}>{sanitizeText(user.name)}</div>
        <div style={styles.userStatus}>{user.online ? 'В сети' : 'Не в сети'}</div>
      </div>
    </div>
  );
});

const ChatWidget: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadFrom, setUnreadFrom] = useState<Record<string, number>>({});
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [sendingFile, setSendingFile] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ name: string; url: string; mimeType: string } | null>(null);
  const [pdfLoadError, setPdfLoadError] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const fabRef = useRef<HTMLButtonElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollInterval = useRef<NodeJS.Timeout>();
  const messagesRef = useRef<Message[]>([]);
  const prevMessagesLengthRef = useRef<number>(0);
  const initialScrollDoneRef = useRef<boolean>(false);
  const isUserScrolledUpRef = useRef<boolean>(false);

  const backendOrigin = useMemo(() => {
    const base = api.defaults.baseURL || '';
    return base.replace(/\/api$/, '');
  }, []);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Получение количества непрочитанных и онлайн-статусов
  const fetchUnreadCount = async () => {
    try {
      const res = await api.get('/chat/unread');
      console.log('[ChatWidget] /chat/unread response:', res.data);
      
      const total = res.data.total ?? 0;
      setUnreadCount(total);
      
      const unreadMap: Record<string, number> = {};
      const fromArray = res.data.from;
      
      if (Array.isArray(fromArray)) {
        fromArray.forEach((item: any) => {
          const userId = String(item._id || item.userId || item.id || '');
          const count = Number(item.count) || 0;
          if (userId && count > 0) {
            unreadMap[userId] = count;
          }
        });
      }
      
      console.log('[ChatWidget] unreadMap built:', unreadMap);
      setUnreadFrom(unreadMap);
      
      if (res.data.onlineUsers && Array.isArray(res.data.onlineUsers)) {
        setUsers(prev => prev.map(u => ({
          ...u,
          online: res.data.onlineUsers.includes(u.id)
        })));
      }
    } catch (err) {
      console.error('Ошибка получения непрочитанных', err);
    }
  };

  // Загрузка списка пользователей (только при открытии чата)
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await api.get('/chat/users');
      setUsers(res.data);
    } catch (err) {
      console.error('Ошибка загрузки пользователей чата', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Загрузка сообщений с выбранным пользователем
  const fetchMessages = async (userId: string, isBackground = false) => {
    if (!userId) return;
    try {
      const res = await api.get(`/chat/messages/${userId}`);
      const newMessages = res.data;
      
      if (isBackground) {
        const prevIds = new Set(messagesRef.current.map(m => m.id));
        const hasNew = newMessages.some((m: Message) => !prevIds.has(m.id));
        if (!hasNew) return;
        
        if (isUserScrolledUpRef.current) {
          setMessages(newMessages);
          return;
        }
      }
      
      setMessages(newMessages);
      initialScrollDoneRef.current = false;
      // После загрузки сообщений обновляем счётчики (сервер должен пометить их прочитанными)
      fetchUnreadCount();
    } catch (err) {
      console.error('Ошибка загрузки сообщений', err);
    }
  };

  // Удаление сообщения
  const deleteMessage = async (messageId: string) => {
    try {
      await api.delete(`/chat/messages/${messageId}`);
      setMessages(prev => prev.filter(m => m.id !== messageId));
    } catch (err) {
      console.error('Ошибка удаления сообщения', err);
      alert('Не удалось удалить сообщение');
    }
  };

  const scrollToBottom = (behavior: 'auto' | 'smooth' = 'auto') => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 10;
    isUserScrolledUpRef.current = !isAtBottom;
  };

  useEffect(() => {
    if (messages.length > 0 && !initialScrollDoneRef.current) {
      requestAnimationFrame(() => {
        scrollToBottom('auto');
        initialScrollDoneRef.current = true;
      });
    } else if (messages.length > 0) {
      const prevLength = prevMessagesLengthRef.current;
      const newLength = messages.length;
      if (newLength > prevLength) {
        const lastMessage = messages[messages.length - 1];
        const isOwn = lastMessage.from === currentUser?.id;
        if (isOwn || !isUserScrolledUpRef.current) {
          requestAnimationFrame(() => scrollToBottom('smooth'));
        }
      }
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages, currentUser?.id]);

  const sendMessage = async () => {
    if (!selectedUser || !newMessage.trim()) return;
    try {
      const res = await api.post('/chat/messages', {
        to: selectedUser.id,
        text: newMessage.trim()
      });
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      fetchUnreadCount();
      isUserScrolledUpRef.current = false;
    } catch (err) {
      console.error('Ошибка отправки сообщения', err);
      alert('Не удалось отправить сообщение');
    }
  };

  const sendFile = async (file: File) => {
    if (!selectedUser) return;
    setSendingFile(true);
    const formData = new FormData();
    formData.append('to', selectedUser.id);
    formData.append('file', file);
    try {
      const res = await api.post('/chat/messages', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMessages(prev => [...prev, res.data]);
      fetchUnreadCount();
      isUserScrolledUpRef.current = false;
    } catch (err) {
      console.error('Ошибка отправки файла', err);
      alert('Не удалось отправить файл');
    } finally {
      setSendingFile(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) sendFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const insertEmoji = (emoji: string) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const decodeFileName = (name: string) => {
    try {
      return decodeURIComponent(escape(name));
    } catch {
      return name;
    }
  };

  const getFullFileUrl = (url: string) => {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `${backendOrigin}${url.startsWith('/') ? url : '/' + url}`;
  };

  const downloadFile = async (file: { name: string; url: string }) => {
    try {
      const fullUrl = getFullFileUrl(file.url);
      const response = await fetch(fullUrl, { credentials: 'include' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = decodeFileName(file.name);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Ошибка скачивания файла:', error);
      window.open(getFullFileUrl(file.url), '_blank');
    }
  };

  const openFilePreview = (file: { name: string; url: string; mimeType: string }) => {
    const fullUrl = getFullFileUrl(file.url);
    setPreviewFile({
      name: decodeFileName(file.name),
      url: fullUrl,
      mimeType: file.mimeType,
    });
    setPdfLoadError(false);
    setImageLoadError(false);
  };

  const closeFilePreview = () => {
    setPreviewFile(null);
    setPdfLoadError(false);
    setImageLoadError(false);
  };

  const handleModalDownload = async () => {
    if (previewFile) {
      await downloadFile({ name: previewFile.name, url: previewFile.url });
    }
  };

  // Загружаем пользователей один раз при открытии чата
  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      fetchUnreadCount();
    } else {
      setSelectedUser(null);
      setMessages([]);
    }
  }, [isOpen]);

  // Фоновый опрос — с паузой когда вкладка скрыта (экономия ресурсов)
  useEffect(() => {
    fetchUnreadCount();

    const tick = () => {
      // Не опрашиваем если вкладка браузера неактивна
      if (document.visibilityState === 'hidden') return;
      fetchUnreadCount();
      if (isOpen && selectedUser) {
        fetchMessages(selectedUser.id, true);
      }
    };

    pollInterval.current = setInterval(tick, 5000);

    // При возврате на вкладку — обновить сразу
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isOpen, selectedUser]);

  // При смене выбранного пользователя загружаем его сообщения (сервер сам отметит их прочитанными)
  useEffect(() => {
    if (selectedUser) {
      setMessages([]);
      initialScrollDoneRef.current = false;
      isUserScrolledUpRef.current = false;
      fetchMessages(selectedUser.id);
    }
  }, [selectedUser]);

  // Закрытие чата по клику вне панели (кроме клика на FAB-кнопку)
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      // Клик внутри панели — игнорируем
      if (panelRef.current && panelRef.current.contains(target)) return;
      // Клик по FAB-кнопке — игнорируем (у неё свой onClick toggle)
      if (fabRef.current && fabRef.current.contains(target)) return;
      // Клик по превью-модалке — игнорируем
      const previewEl = document.querySelector('[data-chat-preview-overlay="true"]');
      if (previewEl && previewEl.contains(target)) return;

      setIsOpen(false);
    };

    // mousedown — сработает до click на других элементах
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  // Auto-grow textarea (растёт вверх, не больше 6 строк)
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  }, [newMessage]);

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <Image size={14} />;
    return <FileText size={14} />;
  };

  const emojis = Array.from(POPULAR_EMOJIS);

  return (
    <>
      <button
        ref={fabRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`chat-fab ${unreadCount > 0 ? 'has-unread' : ''}`}
        style={styles.chatButton}
        title="Чат"
      >
        {unreadCount > 0 && <span style={styles.badge}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
        <MessageCircle size={26} color="white" strokeWidth={2.2} />
      </button>

      {isOpen && (
        <div ref={panelRef} className="chat-panel-anim" style={styles.chatPanel}>
          <div style={styles.header}>
            <span style={styles.title}>
              <img src="/images/10.png" alt="" style={styles.headerLogo} />
              Чат {currentUser?.companyName && <span style={{ marginLeft: '8px', fontSize: '12px', fontWeight: 400 }}>({currentUser.companyName})</span>}
            </span>
            <button onClick={() => setIsOpen(false)} style={styles.closeButton}>
              <X size={18} />
            </button>
          </div>

          <div style={styles.content}>
            <div style={styles.usersList}>
              <div style={styles.usersHeader}>Сотрудники</div>
              {loadingUsers ? (
                <div style={styles.loading}>Загрузка...</div>
              ) : (
                users.map(u => (
                  <UserItem
                    key={u.id}
                    user={u}
                    isSelected={selectedUser?.id === u.id}
                    hasUnread={!!unreadFrom[String(u.id)]}
                    unreadCount={unreadFrom[String(u.id)] || 0}
                    onSelect={() => setSelectedUser(u)}
                  />
                ))
              )}
            </div>

            {selectedUser ? (
              <div style={styles.chatArea}>
                <div style={styles.chatHeader}>
                  <strong>{sanitizeText(selectedUser.name)}</strong>
                  <span style={styles.chatStatus}>{selectedUser.online ? 'В сети' : 'Не в сети'}</span>
                </div>
                <div
                  ref={messagesContainerRef}
                  style={styles.messagesArea}
                  onScroll={handleScroll}
                >
                  {loadingMessages ? (
                    <div style={styles.loading}>Загрузка сообщений...</div>
                  ) : (
                    messages.map(msg => {
                      const isOwn = msg.from === currentUser?.id;
                      const isFromSelected = msg.from === selectedUser?.id;
                      const isImage = msg.file?.mimeType?.startsWith('image/');
                      const isOnlyEmojiMessage = !msg.file && isOnlyEmoji(msg.text);
                      return (
                        <div
                          key={msg.id}
                          className="chat-msg-item"
                          style={{ ...styles.messageWrapper, justifyContent: isOwn ? 'flex-end' : 'flex-start' }}
                        >
                          {!isOwn && (
                            <div style={styles.senderName}>
                              {users.find(u => u.id === msg.from)?.name ? sanitizeText(users.find(u => u.id === msg.from)!.name) : 'Пользователь'}
                            </div>
                          )}
                          {isOnlyEmojiMessage ? (
                            <div style={{ fontSize: '48px', lineHeight: 1, padding: '4px', alignSelf: isOwn ? 'flex-end' : 'flex-start' }}>
                              {msg.text}
                            </div>
                          ) : (
                            <div
                              style={{
                                ...styles.message,
                                background: isOwn
                                  ? 'linear-gradient(135deg, #f5e6ff, #f0dbff)'
                                  : isFromSelected
                                  ? 'rgba(82, 196, 26, 0.15)'
                                  : 'rgba(255,255,255,0.85)',
                                color: isOwn ? '#4a1d6d' : '#333',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                border: isOwn ? '1px solid rgba(255,255,255,0.3)' : '1px solid rgba(255,255,255,0.5)',
                              }}
                            >
                              {msg.text && <div style={styles.messageText}>{sanitizeText(msg.text)}</div>}
                              {msg.file && (
                                <div style={styles.fileCard}>
                                  {isImage ? (
                                    <img
                                      src={getFullFileUrl(msg.file.url)}
                                      alt={decodeFileName(msg.file.name)}
                                      style={styles.fileImagePreview}
                                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                    />
                                  ) : (
                                    <div style={styles.fileIcon}>{getFileIcon(msg.file.mimeType)}</div>
                                  )}
                                  <div style={styles.fileDetails}>
                                    <div style={styles.fileName}>{decodeFileName(msg.file.name)}</div>
                                    <div style={styles.fileSize}>{formatFileSize(msg.file.size)}</div>
                                  </div>
                                  <button onClick={(e) => { e.stopPropagation(); downloadFile(msg.file!); }} style={styles.fileDownloadButton} title="Скачать">
                                    <Download size={16} />
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); openFilePreview(msg.file!); }} style={styles.previewButton} title="Быстрый просмотр">
                                    <Eye size={14} />
                                  </button>
                                </div>
                              )}
                              <div style={styles.messageMeta}>
                                {formatTime(msg.createdAt)}
                                {isOwn && msg.read && <CheckCheck size={12} style={{ marginLeft: 4 }} />}
                                {isOwn && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); deleteMessage(msg.id); }}
                                    style={styles.deleteButton}
                                    title="Удалить сообщение"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>
                <div style={styles.inputArea}>
                  <div style={styles.inputActions}>
                    <button onClick={() => setShowEmojiPicker(!showEmojiPicker)} style={styles.actionButton} title="Эмодзи">
                      <Smile size={18} />
                    </button>
                    <button onClick={() => fileInputRef.current?.click()} style={styles.actionButton} title="Файл">
                      <Paperclip size={18} />
                    </button>
                    <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} />
                  </div>
                  {showEmojiPicker && (
                    <div style={styles.emojiPicker}>
                      {emojis.map(emoji => (
                        <button key={emoji} onClick={() => insertEmoji(emoji)} style={styles.emojiButton}>
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                  <textarea
                    ref={textareaRef}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    placeholder="Введите сообщение..."
                    rows={1}
                    style={styles.input}
                  />
                  <button onClick={sendMessage} style={styles.sendButton}>
                    <Send size={18} />
                  </button>
                </div>
              </div>
            ) : (
              <div style={styles.noChat}>Выберите сотрудника для начала общения</div>
            )}
          </div>
        </div>
      )}

      {previewFile && (
        <div style={styles.previewOverlay} onClick={closeFilePreview}>
          <div style={styles.previewModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.previewHeader}>
              <h3 style={styles.previewTitle}>{previewFile.name}</h3>
              <button onClick={closeFilePreview} style={styles.previewCloseButton}><X size={20} /></button>
            </div>
            <div style={styles.previewBody}>
              {previewFile.mimeType.startsWith('image/') ? (
                <>
                  <img
                    src={previewFile.url}
                    alt={previewFile.name}
                    style={imageLoadError ? { display: 'none' } : styles.previewImage}
                    onLoad={() => setImageLoadError(false)}
                    onError={() => setImageLoadError(true)}
                  />
                  {imageLoadError && (
                    <div style={styles.previewUnsupported}>
                      <Image size={48} color="#2575fc" />
                      <p>Не удалось загрузить изображение. Возможно, файл повреждён или не найден.</p>
                      <button onClick={handleModalDownload} style={styles.previewDownloadButton}>
                        <Download size={16} style={{ marginRight: '8px' }} /> Скачать файл
                      </button>
                    </div>
                  )}
                </>
              ) : previewFile.mimeType === 'application/pdf' ? (
                <>
                  <iframe
                    src={previewFile.url}
                    title={previewFile.name}
                    style={pdfLoadError ? { display: 'none' } : styles.previewIframe}
                    onLoad={() => setPdfLoadError(false)}
                    onError={() => setPdfLoadError(true)}
                  />
                  {pdfLoadError && (
                    <div style={styles.previewUnsupported}>
                      <FileText size={48} color="#2575fc" />
                      <p>Не удалось загрузить PDF. Возможно, файл недоступен или требуется авторизация.</p>
                      <button onClick={handleModalDownload} style={styles.previewDownloadButton}>
                        <Download size={16} style={{ marginRight: '8px' }} /> Скачать файл
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div style={styles.previewUnsupported}>
                  <FileText size={48} color="#2575fc" />
                  <p>Предпросмотр для этого типа файлов недоступен</p>
                  <button onClick={handleModalDownload} style={styles.previewDownloadButton}>
                    <Download size={16} style={{ marginRight: '8px' }} /> Скачать файл
                  </button>
                </div>
              )}
            </div>
            <div style={styles.previewFooter}>
              <button onClick={handleModalDownload} style={styles.previewDownloadButton}>
                <Download size={16} style={{ marginRight: '8px' }} /> Скачать
              </button>
              <button onClick={closeFilePreview} style={styles.previewCancelButton}>Закрыть</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes chat-fab-pulse {
          0% { box-shadow: 0 12px 32px rgba(102,126,234,0.5), 0 0 0 0 rgba(102,126,234,0.5); }
          70% { box-shadow: 0 12px 32px rgba(102,126,234,0.5), 0 0 0 14px rgba(102,126,234,0); }
          100% { box-shadow: 0 12px 32px rgba(102,126,234,0.5), 0 0 0 0 rgba(102,126,234,0); }
        }
        @keyframes chat-panel-in {
          from { opacity: 0; transform: translateY(16px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes chat-msg-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .chat-fab {
          transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s;
        }
        .chat-fab:hover {
          transform: scale(1.08);
          box-shadow: 0 16px 40px rgba(102,126,234,0.55) !important;
        }
        .chat-fab:active { transform: scale(0.96); }
        .chat-fab.has-unread { animation: chat-fab-pulse 2s infinite; }
        .chat-panel-anim { animation: chat-panel-in 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
        .chat-user-item {
          transition: background 0.2s, transform 0.15s;
        }
        .chat-user-item:hover {
          background: rgba(102, 126, 234, 0.12) !important;
          transform: translateX(2px);
        }
        .chat-msg-item { animation: chat-msg-in 0.25s ease-out; }
      `}</style>
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  chatButton: {
    position: 'fixed', bottom: '30px', right: '30px', width: '56px', height: '56px', borderRadius: '28px',
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', border: 'none',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)', cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 10000,
  },
  badge: {
    position: 'absolute', top: '-5px', right: '-5px', background: '#ff4d4f', color: 'white',
    fontSize: '12px', borderRadius: '10px', padding: '2px 6px', minWidth: '18px', textAlign: 'center',
  },
  chatPanel: {
    position: 'fixed', bottom: '100px', right: '30px', width: '550px', height: '600px',
    background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(20px)',
    borderRadius: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
    border: '1px solid rgba(255,255,255,0.4)', display: 'flex', flexDirection: 'column',
    zIndex: 10000, overflow: 'hidden',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px',
    borderBottom: '1px solid rgba(0,0,0,0.05)', backgroundColor: 'rgba(106, 17, 203, 0.05)',
  },
  title: { fontSize: '16px', fontWeight: 600, color: '#1a1a2e', display: 'flex', alignItems: 'center' },
  headerLogo: { height: '24px', width: 'auto', marginRight: '8px' },
  closeButton: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#666', padding: 4, borderRadius: '20px' },
  content: { display: 'flex', flex: 1, overflow: 'hidden' },
  usersList: {
    width: '220px', borderRight: '1px solid rgba(0,0,0,0.05)', overflowY: 'auto',
    backgroundColor: 'rgba(255,255,255,0.3)', padding: '8px 0',
  },
  usersHeader: { padding: '12px 16px', fontSize: '12px', fontWeight: 600, color: '#666', borderBottom: '1px solid rgba(0,0,0,0.05)' },
  userItem: { display: 'flex', alignItems: 'center', padding: '10px 12px', cursor: 'pointer', transition: 'background 0.2s' },
  userAvatarWrapper: { position: 'relative', marginRight: '10px', flexShrink: 0 },
  userAvatar: { width: '40px', height: '40px', borderRadius: '20px', background: '#e6f0fa', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '20px' },
  onlineDot: { position: 'absolute', bottom: '0', right: '0', width: '10px', height: '10px', borderRadius: '5px', border: '2px solid white', zIndex: 2 },
  avatarBadge: {
    position: 'absolute', top: '-4px', right: '-6px', background: '#ff4d4f', color: 'white',
    fontSize: '10px', fontWeight: 'bold', borderRadius: '10px', padding: '2px 5px',
    minWidth: '16px', textAlign: 'center', zIndex: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
  userInfo: { flex: 1, minWidth: 0 },
  userName: { fontSize: '12px', fontWeight: 500, lineHeight: 1.3, wordBreak: 'break-word' },
  userStatus: { fontSize: '9px', color: '#888', marginTop: '2px' },
  chatArea: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  chatHeader: { padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.05)', fontSize: '14px', fontWeight: 500, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  chatStatus: { fontSize: '11px', color: '#888' },
  messagesArea: { flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px', background: 'rgba(255,255,255,0.2)' },
  messageWrapper: { display: 'flex', flexDirection: 'column', maxWidth: '100%' },
  senderName: { fontSize: '10px', color: '#888', marginBottom: '2px', marginLeft: '4px' },
  message: {
    padding: '8px 12px', borderRadius: '18px', fontSize: '14px', wordWrap: 'break-word',
    overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '100%',
    backdropFilter: 'blur(10px)', transition: 'transform 0.2s'
  },
  messageText: { marginBottom: '2px', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word' },
  fileCard: { display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: '12px', padding: '8px 12px', marginTop: '4px', minWidth: '180px', flexWrap: 'wrap' },
  fileImagePreview: { width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px' },
  fileIcon: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', backgroundColor: 'rgba(37,117,252,0.1)', borderRadius: '8px', color: '#2575fc' },
  fileDetails: { flex: 1 },
  fileName: { fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '150px' },
  fileSize: { fontSize: '10px', color: '#888' },
  previewButton: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#2575fc', padding: '4px', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' },
  fileDownloadButton: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#2575fc', padding: '4px', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' },
  deleteButton: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#ff4d4f', padding: '2px', marginLeft: '6px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' },
  previewOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, backdropFilter: 'blur(8px)' },
  previewModal: { background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)', borderRadius: '24px', width: '90%', maxWidth: '800px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.3)' },
  previewHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid rgba(0,0,0,0.1)' },
  previewTitle: { fontSize: '16px', fontWeight: 600, margin: 0, color: '#1a1a2e' },
  previewCloseButton: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#666', padding: '4px', borderRadius: '20px' },
  previewBody: { flex: 1, overflow: 'auto', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' },
  previewImage: { maxWidth: '100%', maxHeight: '50vh', objectFit: 'contain', borderRadius: '8px' },
  previewIframe: { width: '100%', height: '50vh', border: 'none', borderRadius: '8px' },
  previewUnsupported: { textAlign: 'center', color: '#666' },
  previewFooter: { display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 20px', borderTop: '1px solid rgba(0,0,0,0.1)' },
  previewDownloadButton: { background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', color: 'white', border: 'none', borderRadius: '30px', padding: '8px 20px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', fontSize: '14px' },
  previewCancelButton: { background: 'transparent', border: '1px solid #2575fc', color: '#2575fc', borderRadius: '30px', padding: '8px 20px', cursor: 'pointer' },
  messageMeta: { fontSize: '9px', opacity: 0.7, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', marginTop: '2px' },
  inputArea: { display: 'flex', alignItems: 'flex-end', padding: '10px 12px', borderTop: '1px solid rgba(0,0,0,0.05)', gap: '8px', backgroundColor: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(4px)' },
  inputActions: { display: 'flex', gap: '4px' },
  actionButton: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#888', padding: '6px', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  emojiPicker: { position: 'absolute', bottom: '50px', left: '10px', background: 'white', borderRadius: '20px', padding: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px', zIndex: 10, border: '1px solid #eee', width: '280px', maxHeight: '200px', overflowY: 'auto' },
  emojiButton: { background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', padding: '6px', borderRadius: '12px', transition: 'background 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  input: { flex: 1, padding: '10px 14px', borderRadius: '22px', border: '1px solid rgba(102,126,234,0.15)', background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(4px)', fontSize: '13px', outline: 'none', fontFamily: 'inherit', color: '#1a1a2e', resize: 'none', minHeight: '42px', maxHeight: '140px', lineHeight: '1.45', boxSizing: 'border-box', overflowY: 'auto', transition: 'border 0.2s' },
  sendButton: { background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', border: 'none', borderRadius: '50%', width: '42px', height: '42px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white', boxShadow: '0 4px 12px rgba(102,126,234,0.3)', alignSelf: 'flex-end', transition: 'transform 0.2s' },
  loading: { padding: '20px', textAlign: 'center', color: '#888', fontSize: '12px' },
  noChat: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: '14px' },
};

export default ChatWidget;